import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../components/primitives/button";
import { getProfileSummaries } from "../../api/profile";
import {
  getReportDetail,
  listModerationActions,
  listReportsForReview,
  recordModerationAction,
  setUserAccess,
  updateReportStatus,
} from "../../api/reporting";

function getProfileSummary(profileSummaryByUserId, userId) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return null;
  return profileSummaryByUserId.get(normalizedUserId) || null;
}

function formatProfileLocation(profile) {
  const city = String(profile?.location?.city || "").trim();
  const state = String(profile?.location?.state || "").trim();
  const country = String(profile?.location?.country || "").trim();
  return [city, state, country].filter(Boolean).join(", ") || "Unknown";
}

function formatDateTime(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString();
}

function toTimestamp(value) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 0;
  return timestamp;
}

function toHoursAge(value) {
  const ageMs = Date.now() - toTimestamp(value);
  return Math.max(0, ageMs / (1000 * 60 * 60));
}

function formatAge(value) {
  const hours = toHoursAge(value);
  if (hours < 1) {
    return `${Math.max(1, Math.round(hours * 60))}m ago`;
  }
  if (hours < 24) {
    return `${Math.round(hours)}h ago`;
  }
  return `${Math.round(hours / 24)}d ago`;
}

function getToxicityConfidence(report) {
  const reasonCode = String(report?.reasonCode || "").trim().toUpperCase();
  const baseByReason = {
    THREAT: 0.95,
    HATE_SPEECH: 0.92,
    HARASSMENT: 0.8,
    SEXUAL_CONTENT: 0.86,
    IMPERSONATION: 0.62,
    SPAM: 0.45,
    OTHER: 0.52,
  };
  let confidence = baseByReason[reasonCode] ?? 0.5;
  const snippet = String(report?.messageSnippet || "").toLowerCase();
  if (/\b(kill|hurt|die|threat)\b/.test(snippet)) confidence = Math.max(confidence, 0.96);
  if (/\b(slur|hate)\b/.test(snippet)) confidence = Math.max(confidence, 0.92);
  return Math.min(1, Math.max(0, confidence));
}

function getVelocityMetrics(targetReports) {
  const now = Date.now();
  const oneHourAgo = now - (60 * 60 * 1000);
  const dayAgo = now - (24 * 60 * 60 * 1000);
  const lastHour = targetReports.filter((report) => toTimestamp(report?.reportSubmittedAt) >= oneHourAgo).length;
  const lastDay = targetReports.filter((report) => toTimestamp(report?.reportSubmittedAt) >= dayAgo).length;
  const velocityScore = Math.min(1, (lastHour * 0.55) + (lastDay * 0.1));
  return { lastHour, lastDay, velocityScore };
}

function computeRisk(report, targetReports, targetActions) {
  const reportCount = Math.max(1, Number(report?.targetUserReportCount || targetReports.length || 1));
  const toxicityConfidence = getToxicityConfidence(report);
  const repeatOffenderCount = targetActions.filter((entry) => {
    const action = String(entry?.actionType || "").toUpperCase();
    return action.includes("DISABLE") || action === "TEMP_MUTE" || action === "WARN_USER";
  }).length;
  const velocity = getVelocityMetrics(targetReports);
  const score = Math.round(
    Math.min(
      100,
      (reportCount * 7) +
      (toxicityConfidence * 34) +
      (Math.min(repeatOffenderCount, 5) * 9) +
      (velocity.velocityScore * 22)
    )
  );

  let severity = "LOW";
  if (score >= 80) severity = "CRITICAL";
  else if (score >= 60) severity = "HIGH";
  else if (score >= 35) severity = "MEDIUM";

  return {
    score,
    severity,
    toxicityConfidence,
    repeatOffenderCount,
    velocity,
  };
}

function toErrorMessage(error, fallback) {
  const message = String(error?.message || "");
  if (message.includes("Forbidden")) return "You do not have permission for this action.";
  if (message.includes("MFA")) return "MFA verification is required. Please sign in again.";
  if (message.includes("cannot modify their own access")) return "You cannot take enforcement action on your own account.";
  return message || fallback;
}

function buildEscalationEvidence({ report, risk, targetReports, targetActions, reason }) {
  const lines = [
    `Escalation package`,
    `Report: ${report?.reportId || "Unknown"}`,
    `Target: ${report?.targetUserId || "Unknown"}`,
    `Reason code: ${report?.reasonCode || "UNKNOWN"}`,
    `Risk score: ${risk.score} (${risk.severity})`,
    `Toxicity confidence: ${(risk.toxicityConfidence * 100).toFixed(0)}%`,
    `Reports on target: ${targetReports.length}`,
    `Prior moderation actions on target: ${targetActions.length}`,
    `Reporter: ${report?.reporterUserId || "Unknown"}`,
    `Submitted at: ${formatDateTime(report?.reportSubmittedAt)}`,
    `Snippet: ${report?.messageSnippet || "N/A"}`,
    `Moderator note: ${String(reason || "").trim()}`,
  ];
  return lines.join("\n");
}

export default function ModerationWorkspace({
  context,
  currentUserId,
  staffUsers = [],
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [queue, setQueue] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [profileSummaryByUserId, setProfileSummaryByUserId] = useState(new Map());
  const [targetReports, setTargetReports] = useState([]);
  const [targetActions, setTargetActions] = useState([]);
  const [globalActions, setGlobalActions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ACTIVE_CASES");
  const [actionReason, setActionReason] = useState("");
  const [decisionType, setDecisionType] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [muteHours, setMuteHours] = useState(12);

  const canModerate = Boolean(context?.isModerator || context?.isAdmin || context?.isSuperAdmin);
  const canViewAuditLog = Boolean(context?.isAdmin || context?.isSuperAdmin);

  const staffRoleMap = useMemo(() => {
    const nextMap = new Map();
    (staffUsers || []).forEach((entry) => {
      const id = String(entry?.userId || "").trim();
      if (!id) return;
      nextMap.set(id, new Set((entry?.roles || []).map((role) => String(role || "").toUpperCase())));
    });
    return nextMap;
  }, [staffUsers]);

  const loadAll = useCallback(async () => {
    if (!canModerate) return;
    setLoading(true);
    setError("");
    try {
      const reports = await listReportsForReview({ limit: 150 });
      const actions = canViewAuditLog
        ? await listModerationActions({ limit: 200 })
        : [];
      const nextReports = Array.isArray(reports) ? reports : [];
      const nextActions = Array.isArray(actions) ? actions : [];

      setQueue(nextReports);
      setGlobalActions(nextActions);

      if (!nextReports.length) {
        setSelectedReportId("");
        setSelectedReport(null);
      } else {
        const selectedExists = nextReports.some((entry) => entry.reportId === selectedReportId);
        setSelectedReportId(selectedExists ? selectedReportId : nextReports[0].reportId);
      }
    } catch (loadError) {
      console.error("Failed to load moderation workspace:", loadError);
      setError(toErrorMessage(loadError, "Failed to load moderation queue."));
      setQueue([]);
      setGlobalActions([]);
      setSelectedReportId("");
      setSelectedReport(null);
    } finally {
      setLoading(false);
    }
  }, [canModerate, canViewAuditLog, selectedReportId]);

  const loadCase = useCallback(async () => {
    if (!selectedReportId) {
      setSelectedReport(null);
      setTargetReports([]);
      setTargetActions([]);
      return;
    }

    setError("");
    try {
      const detail = await getReportDetail(selectedReportId);
      const targetUserId = String(detail?.targetUserId || "").trim();
      const [relatedReports, relatedActions] = await Promise.all([
        targetUserId
          ? listReportsForReview({ targetUserId, limit: 120 })
          : Promise.resolve([]),
        canViewAuditLog && targetUserId
          ? listModerationActions({ targetUserId, limit: 120 })
          : Promise.resolve([]),
      ]);

      setSelectedReport(detail || null);
      setTargetReports(Array.isArray(relatedReports) ? relatedReports : []);
      setTargetActions(Array.isArray(relatedActions) ? relatedActions : []);
    } catch (caseError) {
      console.error("Failed to load case detail:", caseError);
      setError(toErrorMessage(caseError, "Failed to load case detail."));
      setSelectedReport(null);
      setTargetReports([]);
      setTargetActions([]);
    }
  }, [canViewAuditLog, selectedReportId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    loadCase();
  }, [loadCase]);

  useEffect(() => {
    let cancelled = false;

    async function loadTargetProfiles() {
      const ids = [...new Set(
        queue
          .map((entry) => String(entry?.targetUserId || "").trim())
          .filter(Boolean)
      )];
      if (!ids.length) return;

      try {
        const summaries = await getProfileSummaries(ids.slice(0, 60));
        if (cancelled) return;
        setProfileSummaryByUserId((prev) => {
          const merged = new Map(prev);
          summaries.forEach((value, key) => {
            merged.set(String(key || "").trim(), value);
          });
          return merged;
        });
      } catch (profileError) {
        console.error("Failed to load profile context for moderation queue:", profileError);
      }
    }

    loadTargetProfiles();
    return () => {
      cancelled = true;
    };
  }, [queue]);

  const reportActionsByUser = useMemo(() => {
    const grouped = new Map();
    globalActions.forEach((entry) => {
      const targetUserId = String(entry?.targetUserId || "").trim();
      if (!targetUserId) return;
      const list = grouped.get(targetUserId) || [];
      list.push(entry);
      grouped.set(targetUserId, list);
    });
    return grouped;
  }, [globalActions]);

  const reportsByUser = useMemo(() => {
    const grouped = new Map();
    queue.forEach((entry) => {
      const targetUserId = String(entry?.targetUserId || "").trim();
      if (!targetUserId) return;
      const list = grouped.get(targetUserId) || [];
      list.push(entry);
      grouped.set(targetUserId, list);
    });
    return grouped;
  }, [queue]);

  const filteredQueue = useMemo(() => {
    const working = queue.filter((report) => {
      if (statusFilter === "ALL") return true;
      if (statusFilter === "ACTIVE_CASES") {
        return report.status === "OPEN" || report.status === "IN_REVIEW";
      }
      return String(report.status || "").toUpperCase() === statusFilter;
    });

    return working
      .map((report) => {
        const targetUserId = String(report?.targetUserId || "").trim();
        const userReports = reportsByUser.get(targetUserId) || [];
        const userActions = reportActionsByUser.get(targetUserId) || [];
        const profileSummary = getProfileSummary(profileSummaryByUserId, targetUserId);
        const risk = computeRisk(report, userReports, userActions);
        return {
          ...report,
          _risk: risk,
          _queueKind: report.targetType === "MESSAGE" ? "Chat report" : "User report",
          _toxicityFlag: risk.toxicityConfidence >= 0.8,
          _targetLabel:
            String(profileSummary?.profileName || profileSummary?.profile?.profileName || "").trim() ||
            targetUserId,
        };
      })
      .sort((a, b) => {
        if (b._risk.score !== a._risk.score) return b._risk.score - a._risk.score;
        return toTimestamp(b.reportSubmittedAt) - toTimestamp(a.reportSubmittedAt);
      });
  }, [profileSummaryByUserId, queue, reportActionsByUser, reportsByUser, statusFilter]);

  const selectedRisk = useMemo(() => computeRisk(selectedReport, targetReports, targetActions), [selectedReport, targetReports, targetActions]);

  const selectedTargetUserId = String(selectedReport?.targetUserId || "").trim();
  const selectedProfileSummary = getProfileSummary(profileSummaryByUserId, selectedTargetUserId);
  const selectedProfile = selectedProfileSummary?.profile || null;
  const selectedProfileName =
    String(selectedProfileSummary?.profileName || selectedProfile?.profileName || "").trim() ||
    selectedTargetUserId ||
    "Unknown";
  const selectedProfileStory = String(selectedProfile?.story || "").trim();
  const selectedTargetRoleSet = staffRoleMap.get(selectedTargetUserId) || new Set();
  const targetIsSuperAdmin = selectedTargetRoleSet.has("SUPERADMIN");
  const actingOnSelf = Boolean(currentUserId) && selectedTargetUserId === String(currentUserId || "").trim();
  const actionBlocked = actingOnSelf || targetIsSuperAdmin;

  const openCases = useMemo(
    () => queue.filter((entry) => entry.status === "OPEN" || entry.status === "IN_REVIEW"),
    [queue]
  );

  const slaBuckets = useMemo(() => {
    const buckets = {
      under1h: 0,
      between1hAnd24h: 0,
      over24h: 0,
    };
    openCases.forEach((report) => {
      const age = toHoursAge(report?.reportSubmittedAt);
      if (age < 1) buckets.under1h += 1;
      else if (age < 24) buckets.between1hAnd24h += 1;
      else buckets.over24h += 1;
    });
    return buckets;
  }, [openCases]);

  const analytics = useMemo(() => {
    const closed = queue.filter((entry) => entry.status === "RESOLVED" || entry.status === "DISMISSED");
    const resolutionHours = closed
      .map((entry) => {
        const start = toTimestamp(entry?.reportSubmittedAt);
        const end = toTimestamp(entry?.lastStatusUpdateAt);
        if (!start || !end || end < start) return null;
        return (end - start) / (1000 * 60 * 60);
      })
      .filter((value) => Number.isFinite(value));
    const avgResolutionHours = resolutionHours.length
      ? (resolutionHours.reduce((sum, value) => sum + value, 0) / resolutionHours.length)
      : null;

    const now = Date.now();
    const oneDayAgo = now - (24 * 60 * 60 * 1000);
    const twoDaysAgo = now - (48 * 60 * 60 * 1000);
    const recentBacklog = openCases.filter((entry) => toTimestamp(entry?.reportSubmittedAt) >= oneDayAgo).length;
    const previousBacklog = openCases.filter((entry) => {
      const submitted = toTimestamp(entry?.reportSubmittedAt);
      return submitted >= twoDaysAgo && submitted < oneDayAgo;
    }).length;
    const backlogTrendDelta = recentBacklog - previousBacklog;

    const actionCounts = {};
    globalActions.forEach((entry) => {
      const actionType = String(entry?.actionType || "UNKNOWN");
      actionCounts[actionType] = (actionCounts[actionType] || 0) + 1;
    });
    const sortedActions = Object.entries(actionCounts).sort((a, b) => b[1] - a[1]);
    const topAction = sortedActions[0] || null;
    const totalActions = Math.max(1, globalActions.length);
    const topActionShare = topAction ? (topAction[1] / totalActions) : 0;

    return {
      avgResolutionHours,
      backlogTrendDelta,
      topAction,
      topActionShare,
    };
  }, [globalActions, openCases, queue]);

  const runAction = async ({ actionKey, runner }) => {
    setError("");
    setActionLoading(actionKey);
    try {
      await runner();
      await loadAll();
      await loadCase();
    } catch (actionError) {
      console.error("Failed moderation action:", actionError);
      setError(toErrorMessage(actionError, "Moderation action failed."));
    } finally {
      setActionLoading("");
    }
  };

  const ensureReason = () => {
    const trimmed = String(actionReason || "").trim();
    if (trimmed.length < 8) {
      setError("A reason of at least 8 characters is required.");
      return null;
    }
    return trimmed;
  };

  const guardAction = () => {
    if (!selectedTargetUserId) {
      setError("Select a report first.");
      return false;
    }
    if (actionBlocked) {
      setError(actingOnSelf ? "You cannot moderate your own account." : "Peer SuperAdmins cannot be actioned.");
      return false;
    }
    return true;
  };

  const decisionOptions = useMemo(() => {
    return [
      {
        key: "WARN",
        label: "Warn User",
        help: "Issue warning and keep account active.",
      },
      {
        key: "TEMP_MUTE",
        label: "Temporary Mute",
        help: "Record a temporary mute window.",
      },
      {
        key: "TOXIC_BAN",
        label: "Ban Toxic Account",
        help: "Permanently disable account for toxic behavior.",
      },
      {
        key: "ESCALATE",
        label: "Escalate to Admin",
        help: "Package evidence for admin review.",
      },
      {
        key: "CASE_DISMISS",
        label: "Dismiss Case",
        help: "Close case as invalid report.",
      },
    ];
  }, []);

  const selectedDecision = useMemo(
    () => decisionOptions.find((entry) => entry.key === decisionType) || null,
    [decisionOptions, decisionType]
  );

  const submitDecision = async () => {
    if (!selectedDecision) {
      setError("Select an action to apply.");
      return;
    }
    if (!guardAction()) return;

    const reason = ensureReason();
    if (!reason) return;

    if (!window.confirm(`Apply "${selectedDecision.label}" for ${selectedProfileName}?`)) return;

    switch (selectedDecision.key) {
      case "WARN":
        await runAction({
          actionKey: "warn",
          runner: async () => {
            await recordModerationAction({
              targetUserId: selectedTargetUserId,
              actionType: "WARN_USER",
              reportId: selectedReportId || null,
              notes: reason,
            });
            await updateReportStatus({
              reportId: selectedReportId,
              status: "RESOLVED",
              adminNotes: reason,
            });
          },
        });
        break;
      case "TEMP_MUTE":
        await runAction({
          actionKey: "mute",
          runner: async () => {
            await recordModerationAction({
              targetUserId: selectedTargetUserId,
              actionType: "TEMP_MUTE",
              reportId: selectedReportId || null,
              notes: `[Duration ${muteHours}h] ${reason}`,
            });
            await updateReportStatus({
              reportId: selectedReportId,
              status: "RESOLVED",
              adminNotes: reason,
            });
          },
        });
        break;
      case "TOXIC_BAN":
        await runAction({
          actionKey: "toxic-ban",
          runner: async () => {
            await setUserAccess({
              userId: selectedTargetUserId,
              status: "DISABLED",
              reasonCode: "TOXIC_BAN",
              reasonText: reason,
              relatedReportId: selectedReportId || null,
            });
            await updateReportStatus({
              reportId: selectedReportId,
              status: "RESOLVED",
              adminNotes: reason,
            });
          },
        });
        break;
      case "ESCALATE": {
        const evidence = buildEscalationEvidence({
          report: selectedReport,
          risk: selectedRisk,
          targetReports,
          targetActions,
          reason,
        });
        await runAction({
          actionKey: "escalate",
          runner: async () => {
            await recordModerationAction({
              targetUserId: selectedTargetUserId,
              actionType: "ESCALATED_TO_ADMIN",
              reportId: selectedReportId || null,
              notes: evidence,
            });
            await updateReportStatus({
              reportId: selectedReportId,
              status: "IN_REVIEW",
              adminNotes: reason,
            });
          },
        });
        break;
      }
      case "CASE_DISMISS":
        await runAction({
          actionKey: "case-dismiss",
          runner: async () => {
            await recordModerationAction({
              targetUserId: selectedTargetUserId,
              actionType: "REPORT_INVALID",
              reportId: selectedReportId || null,
              notes: reason,
            });
            await updateReportStatus({
              reportId: selectedReportId,
              status: "DISMISSED",
              adminNotes: reason,
            });
          },
        });
        break;
      default:
        setError("Unsupported action selected.");
        return;
    }

    setActionReason("");
    setDecisionType("");
  };

  const profileContext = useMemo(() => {
    const total = targetReports.length;
    const open = targetReports.filter((entry) => entry.status === "OPEN" || entry.status === "IN_REVIEW").length;
    const reporters = new Set(targetReports.map((entry) => String(entry?.reporterUserId || "").trim()).filter(Boolean));
    const surfaces = targetReports.reduce((acc, entry) => {
      const surface = String(entry?.sourceSurface || "OTHER").toUpperCase();
      acc[surface] = (acc[surface] || 0) + 1;
      return acc;
    }, {});
    return {
      total,
      open,
      reporterCount: reporters.size,
      surfaces,
    };
  }, [targetReports]);

  const caseTimeline = useMemo(() => {
    const reportItems = targetReports.map((entry) => ({
      id: `report-${entry.reportId}`,
      type: "report",
      timestamp: entry.reportSubmittedAt,
      label: `${entry.status || "OPEN"} report ${entry.reportId || ""}`.trim(),
      detail: entry.messageSnippet || entry.reasonText || entry.reasonCode || "No additional context.",
    }));
    const actionItems = targetActions.map((entry) => ({
      id: `action-${entry.actionId}`,
      type: "action",
      timestamp: entry.actedAt,
      label: `${entry.actionType || "ACTION"} by ${entry.adminUserName || entry.adminUserId || "Staff"}`,
      detail: entry.notes || "No notes.",
    }));

    return [...reportItems, ...actionItems].sort((a, b) => toTimestamp(b.timestamp) - toTimestamp(a.timestamp));
  }, [targetActions, targetReports]);

  if (!canModerate) {
    return (
      <section className="opsConsole__section">
        <h2>Moderation</h2>
        <p className="opsConsole__subtle">Requires Moderator, Admin, or SuperAdmin.</p>
      </section>
    );
  }

  return (
    <section className="opsConsole__section">
      <div className="opsConsole__sectionHead">
        <h2>Moderation Queue</h2>
        <div className="opsConsole__sectionActions">
          <select
            className="opsConsole__filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            disabled={loading || Boolean(actionLoading)}
          >
            <option value="ACTIVE_CASES">Active cases</option>
            <option value="ALL">All cases</option>
            <option value="OPEN">Open</option>
            <option value="IN_REVIEW">In review</option>
            <option value="RESOLVED">Resolved</option>
            <option value="DISMISSED">Dismissed</option>
          </select>
          <Button variant="outline" onClick={loadAll} disabled={loading || Boolean(actionLoading)}>
            {loading ? "Loading..." : "Refresh"}
          </Button>
        </div>
      </div>

      <div className="opsConsole__analyticsRow">
        <div className="opsConsole__metricCard">
          <div className="opsConsole__metricLabel">SLA — Under 1h</div>
          <div className="opsConsole__metricValue">{slaBuckets.under1h}</div>
        </div>
        <div className="opsConsole__metricCard">
          <div className="opsConsole__metricLabel">SLA — 1–24h</div>
          <div className="opsConsole__metricValue">{slaBuckets.between1hAnd24h}</div>
        </div>
        <div className="opsConsole__metricCard">
          <div className="opsConsole__metricLabel">SLA — Over 24h</div>
          <div className={`opsConsole__metricValue ${slaBuckets.over24h > 0 ? "opsConsole__metricValue--warn" : ""}`}>
            {slaBuckets.over24h}
          </div>
        </div>
        <div className="opsConsole__metricCard">
          <div className="opsConsole__metricLabel">Avg resolution</div>
          <div className="opsConsole__metricValue">
            {analytics.avgResolutionHours == null ? "N/A" : `${analytics.avgResolutionHours.toFixed(1)}h`}
          </div>
        </div>
        <div className="opsConsole__metricCard">
          <div className="opsConsole__metricLabel">Backlog trend (24h)</div>
          <div className={`opsConsole__metricValue ${analytics.backlogTrendDelta > 0 ? "opsConsole__metricValue--warn" : analytics.backlogTrendDelta < 0 ? "opsConsole__metricValue--ok" : ""}`}>
            {analytics.backlogTrendDelta > 0 ? "+" : ""}{analytics.backlogTrendDelta}
          </div>
        </div>
        <div className="opsConsole__metricCard">
          <div className="opsConsole__metricLabel">Top action</div>
          <div className="opsConsole__metricValue opsConsole__metricValue--sm">
            {analytics.topAction ? `${analytics.topAction[0]} (${(analytics.topActionShare * 100).toFixed(0)}%)` : "N/A"}
          </div>
        </div>
      </div>

      {error ? <div className="opsConsole__error">{error}</div> : null}
      {actionBlocked ? (
        <div className="opsConsole__inlineWarn">
          You cannot act on {actingOnSelf ? "your own account" : "a peer SuperAdmin"}.
        </div>
      ) : null}

      <div className="opsConsole__moderationGrid">
        <div className="opsConsole__queueCol">
          <div className="opsConsole__queueList">
            {filteredQueue.map((entry) => (
              <button
                type="button"
                key={entry.reportId}
                className={`opsConsole__queueRow ${
                  selectedReportId === entry.reportId ? "opsConsole__queueRow--active" : ""
                }`}
                onClick={() => setSelectedReportId(entry.reportId)}
              >
                <div className="opsConsole__queueTop">
                  <span className={`opsConsole__severity opsConsole__severity--${entry._risk.severity.toLowerCase()}`}>
                    {entry._risk.severity}
                  </span>
                  <span className="opsConsole__queueDate">{formatDateTime(entry.reportSubmittedAt)}</span>
                </div>
                <div className="opsConsole__queueMeta">
                  {entry._queueKind} {entry._toxicityFlag ? "• Toxicity flag" : ""}
                </div>
                <div className="opsConsole__queueMeta">
                  Risk {entry._risk.score} • Target {entry._targetLabel}
                </div>
                <div className="opsConsole__queueMeta">
                  {entry.reasonCode || "UNKNOWN"} • {entry.sourceSurface || "OTHER"}
                </div>
                <div className="opsConsole__queueMeta">Status {entry.status}</div>
              </button>
            ))}
            {!loading && filteredQueue.length === 0 ? (
              <div className="opsConsole__status">No cases in this view.</div>
            ) : null}
          </div>
        </div>

        <div className="opsConsole__caseCol">
          {!selectedReport ? (
            <div className="opsConsole__status">Select a case to review.</div>
          ) : (
            <>
              <div className="opsConsole__caseSummaryRow">
                <div className="opsConsole__summaryCard">
                  <div className="opsConsole__summaryLabel">Reason</div>
                  <div className="opsConsole__summaryValue">{selectedReport.reasonCode || "UNKNOWN"}</div>
                </div>
                <div className="opsConsole__summaryCard">
                  <div className="opsConsole__summaryLabel">Risk</div>
                  <div className="opsConsole__summaryValue">
                    {selectedRisk.score} ({selectedRisk.severity})
                  </div>
                </div>
                <div className="opsConsole__summaryCard">
                  <div className="opsConsole__summaryLabel">Target</div>
                  <div className="opsConsole__summaryValue">{selectedProfileName}</div>
                </div>
                <div className="opsConsole__summaryCard">
                  <div className="opsConsole__summaryLabel">Case Age</div>
                  <div className="opsConsole__summaryValue">{formatAge(selectedReport.reportSubmittedAt)}</div>
                </div>
              </div>

                <div className="opsConsole__detailGrid">
                  <div><strong>Status:</strong> {selectedReport.status}</div>
                  <div><strong>Source:</strong> {selectedReport.sourceSurface || "N/A"}</div>
                  <div><strong>Toxicity confidence:</strong> {(selectedRisk.toxicityConfidence * 100).toFixed(0)}%</div>
                  <div><strong>Prior actions:</strong> {selectedRisk.repeatOffenderCount}</div>
                  <div><strong>Profile bio:</strong> {selectedProfileStory || "No bio provided."}</div>
                  <div><strong>Evidence:</strong> {selectedReport.messageSnippet || selectedReport.reasonText || "No additional evidence attached."}</div>
                </div>

              <details className="opsConsole__detailsBlock">
                <summary>Case details</summary>
                <div className="opsConsole__detailGrid">
                  <div><strong>Case ID:</strong> {selectedReport.reportId}</div>
                  <div><strong>Submitted:</strong> {formatDateTime(selectedReport.reportSubmittedAt)}</div>
                  <div><strong>Target user ID:</strong> {selectedReport.targetUserId}</div>
                  <div><strong>Reporter:</strong> {selectedReport.reporterUserId}</div>
                  <div><strong>Velocity (1h / 24h):</strong> {selectedRisk.velocity.lastHour} / {selectedRisk.velocity.lastDay}</div>
                  <div><strong>Profile reports:</strong> {profileContext.total}</div>
                  <div><strong>Unique reporters:</strong> {profileContext.reporterCount}</div>
                  <div><strong>Open incidents:</strong> {profileContext.open}</div>
                  <div><strong>Location:</strong> {formatProfileLocation(selectedProfile)}</div>
                  <div><strong>Interests:</strong> {Array.isArray(selectedProfile?.interests) && selectedProfile.interests.length ? selectedProfile.interests.join(", ") : "N/A"}</div>
                </div>
                <div className="opsConsole__snippetCard">
                  <strong>Profile Bio</strong>
                  <p>{selectedProfileStory || "No bio provided."}</p>
                </div>
                <div className="opsConsole__snippetCard">
                  <strong>Evidence</strong>
                  <p>{selectedReport.messageSnippet || selectedReport.reasonText || "No additional evidence attached."}</p>
                </div>
              </details>

              <section className="opsConsole__decisionPanel">
                <h3 className="opsConsole__auditTitle">Decision</h3>
                <p className="opsConsole__subtle">Choose an action, add a reason, and submit.</p>

                <div className="opsConsole__decisionGrid">
                  {decisionOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className={`opsConsole__decisionBtn ${decisionType === option.key ? "opsConsole__decisionBtn--active" : ""}`}
                      onClick={() => setDecisionType(option.key)}
                      disabled={Boolean(actionLoading)}
                    >
                      <span className="opsConsole__decisionLabel">{option.label}</span>
                      <span className="opsConsole__decisionHelp">{option.help}</span>
                    </button>
                  ))}
                </div>

                {decisionType === "TEMP_MUTE" ? (
                  <div className="opsConsole__inlineControl">
                    <label htmlFor="temp-mute-hours">Mute duration (hours)</label>
                    <input
                      id="temp-mute-hours"
                      type="number"
                      min="1"
                      max="168"
                      value={muteHours}
                      onChange={(event) => setMuteHours(Math.max(1, Math.min(168, Number(event.target.value) || 1)))}
                    />
                  </div>
                ) : null}

                <label className="opsConsole__reasonLabel" htmlFor="moderation-reason">
                  Reason (required)
                </label>
                <textarea
                  id="moderation-reason"
                  className="opsConsole__reasonInput"
                  value={actionReason}
                  onChange={(event) => setActionReason(event.target.value)}
                  placeholder="Cite the evidence and policy."
                  maxLength={1600}
                />
                <div className="opsConsole__reasonFooter">
                  <span className={`opsConsole__reasonStatus ${String(actionReason || "").trim().length >= 8 ? "opsConsole__reasonStatus--ok" : ""}`}>
                    {String(actionReason || "").trim().length >= 8 ? "Ready" : "At least 8 characters"}
                  </span>
                  <span className="opsConsole__reasonCount">{String(actionReason || "").trim().length}/1600</span>
                </div>

                <div className="opsConsole__submitArea">
                  <div className="opsConsole__selectionSummary">
                    {selectedDecision
                      ? `Pending: ${selectedDecision.label} on ${selectedProfileName}`
                      : "Select an action to continue."}
                  </div>
                  {selectedReport.status === "RESOLVED" || selectedReport.status === "DISMISSED" ? (
                    <Button
                      variant="outline"
                      disabled={Boolean(actionLoading)}
                      onClick={async () => {
                        const reopenReason =
                          String(actionReason || "").trim() || "Case reopened for additional review.";
                        if (!window.confirm("Reopen this closed case?")) return;
                        await runAction({
                          actionKey: "case-reopen",
                          runner: () =>
                            updateReportStatus({
                              reportId: selectedReportId,
                              status: "OPEN",
                              adminNotes: reopenReason,
                            }),
                        });
                      }}
                    >
                      {actionLoading === "case-reopen" ? "Reopening..." : "Reopen"}
                    </Button>
                  ) : null}
                  <Button
                    variant="primary"
                    disabled={!selectedDecision || Boolean(actionLoading)}
                    onClick={submitDecision}
                  >
                    {actionLoading ? "Submitting..." : "Apply Decision"}
                  </Button>
                </div>
              </section>

              <h3 className="opsConsole__auditTitle">Timeline</h3>
              <div className="opsConsole__timeline">
                {caseTimeline.map((entry) => (
                  <div key={entry.id} className="opsConsole__timelineItem">
                    <div className="opsConsole__timelineTop">
                      <span>{entry.label}</span>
                      <span>{formatDateTime(entry.timestamp)}</span>
                    </div>
                    <div className="opsConsole__timelineDetail">{entry.detail}</div>
                  </div>
                ))}
                {!caseTimeline.length ? <div className="opsConsole__status">No timeline events.</div> : null}
              </div>
            </>
          )}
        </div>
      </div>

    </section>
  );
}
